import { fromScaled, toScaled } from '@colplat/protocol';
import { byId, groupBy, type BudgetRow, type Ledger } from './rows.js';
import { descendantIds } from './wallets.js';

/**
 * Budgets: a spending limit for one tagged project, out of one wallet
 * (ADR-0024), computed from rows so that it computes in both places
 * (ADR-0025).
 *
 * Spend is the same arithmetic the reports page performs under a tag filter —
 * deliberately, byte for byte, because a budget links through to that filtered
 * list and a figure that disagreed with the list behind it would discredit
 * both.
 *
 * Two things are reported rather than hidden, both consequences of a tag not
 * being a partition the way a wallet is: spending funded from outside the
 * budget's wallet, and spending that counts in full against another budget
 * too.
 */

/** The account types whose legs are a tagged project's COST. */
const COUNTED: ReadonlySet<string> = new Set(['EXPENSE', 'INCOME']);

export interface CostRow {
  tagId: string;
  transactionId: string;
  txDate: string;
  currencyCode: string;
  total: string;
}

export type WalletsByTransaction = ReadonlyMap<string, ReadonlySet<string | null>>;

export interface BudgetDto extends BudgetRow {
  spent: Record<string, string>;
  count: number;
  outsideWallet: { count: number; totals: Record<string, string> };
  sharedWith: { count: number; totals: Record<string, string>; budgetIds: string[] };
}

function addTo(target: Map<string, bigint>, ccy: string, amount: bigint): void {
  target.set(ccy, (target.get(ccy) ?? 0n) + amount);
}

function fixed(map: Map<string, bigint>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [ccy, v] of map) {
    if (v === 0n) continue;
    out[ccy] = fromScaled(v);
  }
  return out;
}

/**
 * Wallet ids a budget covers: the one it names and everything inside it — a
 * limit on Renovation is a limit on Renovation, sub-envelopes and all.
 */
export function walletScope(
  wallets: readonly { id: string; parentId: string | null }[], rootId: string,
): Set<string> {
  return new Set([rootId, ...descendantIds(wallets as never, rootId)]);
}

function inRange(txDate: string, from: string | null, to: string | null): boolean {
  if (from && txDate < from) return false;
  if (to && txDate > to) return false;
  return true;
}

/** The cost of every transaction carrying one of `tagIds`, per currency. */
export function costsOfTags(ledger: Ledger, tagIds: ReadonlySet<string>): CostRow[] {
  if (tagIds.size === 0) return [];
  const accounts = byId(ledger.accounts());
  const transactions = byId(ledger.transactions());
  const legsByTx = groupBy(ledger.splits(), (s) => s.transactionId);

  const out: CostRow[] = [];
  for (const link of ledger.transactionTags()) {
    if (!tagIds.has(link.tagId)) continue;
    const tx = transactions.get(link.transactionId);
    if (!tx) continue;
    const perCurrency = new Map<string, bigint>();
    for (const leg of legsByTx.get(link.transactionId) ?? []) {
      const account = accounts.get(leg.accountId);
      if (!account || !COUNTED.has(account.type)) continue;
      addTo(perCurrency, leg.currencyCode, toScaled(leg.amount));
    }
    for (const [currencyCode, total] of perCurrency) {
      out.push({
        tagId: link.tagId,
        transactionId: link.transactionId,
        txDate: tx.txDate,
        currencyCode,
        total: fromScaled(total),
      });
    }
  }
  return out;
}

/** Which wallets each transaction's money legs sat in. A NULL leg contributes `null`. */
export function walletsByTransaction(ledger: Ledger): Map<string, Set<string | null>> {
  const accounts = byId(ledger.accounts());
  const out = new Map<string, Set<string | null>>();
  for (const leg of ledger.splits()) {
    const account = accounts.get(leg.accountId);
    if (!account || (account.type !== 'ASSET' && account.type !== 'LIABILITY')) continue;
    const set = out.get(leg.transactionId) ?? new Set<string | null>();
    set.add(leg.walletId);
    out.set(leg.transactionId, set);
  }
  return out;
}

export function buildBudgetDtos(
  budgets: readonly BudgetRow[],
  costs: readonly CostRow[],
  walletsByTx: WalletsByTransaction,
  wallets: readonly { id: string; parentId: string | null }[],
): BudgetDto[] {
  const scopes = new Map<string, Set<string> | null>();
  for (const b of budgets) {
    scopes.set(b.id, b.walletId === null ? null : walletScope(wallets, b.walletId));
  }

  const spent = new Map<string, Map<string, bigint>>();
  const outside = new Map<string, Map<string, bigint>>();
  const counted = new Map<string, Set<string>>();
  const outsideTx = new Map<string, Set<string>>();
  /** transaction id -> the budgets that counted it, which is how overlap is found. */
  const claimedBy = new Map<string, Set<string>>();

  for (const b of budgets) {
    spent.set(b.id, new Map());
    outside.set(b.id, new Map());
    counted.set(b.id, new Set());
    outsideTx.set(b.id, new Set());
  }

  for (const cost of costs) {
    const value = toScaled(cost.total);
    for (const b of budgets) {
      if (b.tagId !== cost.tagId) continue;
      if (!inRange(cost.txDate, b.fromDate, b.toDate)) continue;

      const scope = scopes.get(b.id) ?? null;
      // `null` in the set is an un-earmarked money leg. It is a real state
      // (ADR-0023) and it is not this wallet, so a budget that names a wallet
      // does not count it — but it is reported rather than dropped.
      const funded = scope === null
        || [...(walletsByTx.get(cost.transactionId) ?? [])].some((w) => w !== null && scope.has(w));

      if (funded) {
        addTo(spent.get(b.id)!, cost.currencyCode, value);
        counted.get(b.id)!.add(cost.transactionId);
        const claims = claimedBy.get(cost.transactionId) ?? new Set<string>();
        claims.add(b.id);
        claimedBy.set(cost.transactionId, claims);
      } else {
        addTo(outside.get(b.id)!, cost.currencyCode, value);
        outsideTx.get(b.id)!.add(cost.transactionId);
      }
    }
  }

  // Overlap, second pass: a transaction counted by more than one budget is
  // counted IN FULL by each of them. Nothing here corrects for that — see
  // ADR-0024 on why it is displayed instead.
  const sharedTotals = new Map<string, Map<string, bigint>>();
  const sharedTx = new Map<string, Set<string>>();
  const sharedWith = new Map<string, Set<string>>();
  for (const b of budgets) {
    sharedTotals.set(b.id, new Map());
    sharedTx.set(b.id, new Set());
    sharedWith.set(b.id, new Set());
  }
  for (const cost of costs) {
    const claims = claimedBy.get(cost.transactionId);
    if (!claims || claims.size < 2) continue;
    for (const id of claims) {
      const b = budgets.find((x) => x.id === id);
      if (!b || b.tagId !== cost.tagId) continue;
      if (!inRange(cost.txDate, b.fromDate, b.toDate)) continue;
      addTo(sharedTotals.get(id)!, cost.currencyCode, toScaled(cost.total));
      sharedTx.get(id)!.add(cost.transactionId);
      for (const other of claims) if (other !== id) sharedWith.get(id)!.add(other);
    }
  }

  return budgets.map((b) => ({
    ...b,
    spent: fixed(spent.get(b.id)!),
    count: counted.get(b.id)!.size,
    outsideWallet: {
      count: outsideTx.get(b.id)!.size,
      totals: fixed(outside.get(b.id)!),
    },
    sharedWith: {
      count: sharedTx.get(b.id)!.size,
      totals: fixed(sharedTotals.get(b.id)!),
      budgetIds: [...sharedWith.get(b.id)!].sort(),
    },
  }));
}

/** Every budget with its spend, from rows. */
export function budgetsView(ledger: Ledger): BudgetDto[] {
  const budgets = ledger.budgets();
  const costs = costsOfTags(ledger, new Set(budgets.map((b) => b.tagId)));
  return buildBudgetDtos(budgets, costs, walletsByTransaction(ledger), ledger.wallets());
}
